/**
 * The English catalogue is the source of truth for every interface message: its keys are the
 * MessageKey union, so a catalogue that misses a key does not compile. Engine-produced prose is
 * not here; it is keyed by its English source string in ./record.
 */
export const en = {
  "app.title": "ImageForge — Android Image Patcher",
  "app.loading": "Loading workspace",

  "shell.appearance": "Appearance",
  "shell.language": "Language",
  "shell.settings": "Settings",
  "shell.source": "Source",
  "shell.theme.light": "Light",
  "shell.theme.dark": "Dark",
  "shell.theme.system": "System",
  "shell.aria.settings": "Open settings",
  "shell.aria.theme": "Change theme",
  "shell.aria.language": "Change language",
  "shell.aria.workflow": "Workflow progress",
  "shell.aria.close": "Close",
  "shell.footer.privacy": "Local-first · Private · Open Source — images never leave your browser.",
  "shell.footer.description":
    "ImageForge analyzes, patches, repacks and verifies Android images. It never flashes a device.",

  "step.image": "Image",
  "step.analyze": "Analyze",
  "step.patch": "Patch",
  "step.verify": "Verify",
  "step.result": "Result",

  "home.title": "Android Image Patcher",
  "home.subtitle": "Analyze, patch, repack and verify Android images locally in your browser.",
  "home.highlight.local": "Local-first",
  "home.highlight.local.detail": "Patching runs in your browser",
  "home.highlight.private": "Private",
  "home.highlight.private.detail": "Images are never uploaded",
  "home.highlight.open": "Open Source",
  "home.highlight.open.detail": "AGPL-3.0-or-later",
  "home.localBadge": "Local workspace",
  "home.primary.badge": "Recommended",
  "home.flow.title": "How it works",
  "home.flow.image": "Open an image",
  "home.flow.image.detail": "Drop boot, init_boot or vendor_boot.",
  "home.flow.choose": "Choose a method",
  "home.flow.choose.detail": "See what this exact image supports.",
  "home.flow.patch": "Patch and verify",
  "home.flow.patch.detail": "The work stays inside a Web Worker.",
  "home.flow.download": "Download the result",
  "home.flow.download.detail": "Nothing is flashed automatically.",
  "home.more.description": "More local-first tools for images and packages.",
  "picker.limit":
    "Images are processed with the Web Worker + WASM pipeline and are limited to {size}. ImageForge never flashes a device.",

  "tools.title": "Android image tools",
  "tools.subtitle": "Everything runs in your browser: images and packages never leave this device.",
  "tools.more": "More tools",
  "tools.badge.available": "Available",
  "tools.badge.planned": "Planned",
  "tool.patch.title": "Patch an image",
  "tool.patch.description": "Analyze a boot image, plan a patch, verify the result and download it.",
  "tool.extract.title": "Extract from a package",
  "tool.extract.description": "Open an OTA payload.bin or a vendor image archive and pull out boot, init_boot or vendor_boot.",
  "tool.unpack.title": "Unpack partitions",
  "tool.unpack.description": "Open a super image or a sparse image, see the logical partitions inside it and export one.",
  "tool.logo.title": "Boot logo (first screen)",
  "tool.logo.description": "Read the splash image a device shows at boot, view the pictures in it and replace one.",
  "tool.inspect.title": "Inspect an image",
  "tool.inspect.description": "Look at a boot image without changing anything: sections, ramdisk entries, existing patch marks.",
  "notfound.body": "Pick a tool on the start page — the patcher starts with an image you drop in.",

  "detect.title": "What you opened",
  "detect.container": "Container",
  "detect.content": "Content",
  "detect.tools": "Tools for this file",
  "detect.none": "No tool here handles this kind of file yet. It stays in your browser.",
  "reason.tool.wrongKind": "This tool works on {accepts}.",
  "reason.tool.planned": "Not implemented in this build yet.",

  "extract.openHint": "Drop an OTA payload.bin or a vendor image archive here.",
  "extract.from": "from {container}",
  "extract.entries": "What is inside",
  "extract.hint": "A payload names its own partitions; what an entry really is gets detected after it is extracted.",
  "extract.reading": "Reading the package…",
  "extract.extract": "Extract",
  "extract.needsSource": "This entry is stored as a delta: extracting it needs the image it was generated against.",
  "artifact.streamed": "Kept as a blob, not in memory: downloading streams it from the worker.",
  "extract.extracted": "Extracted files",
  "extract.empty": "Nothing extracted yet. Pick an entry above.",
  "extract.useInPatcher": "Use in the patcher",
  "extract.download": "Download",

  "unpack.hint": "Drop a sparse image, a super image or an erofs filesystem image here.",
  "unpack.blockSize": "Block size",
  "unpack.blocks": "Blocks",
  "unpack.chunks": "Chunks",
  "unpack.output": "Unpacks to",
  "unpack.unpack": "Unpack to a raw image",
  "unpack.partitions": "Logical partitions",
  "unpack.metadata": "{size} per metadata slot · {slots} slots · logical block {block}",
  "unpack.name": "Name",
  "unpack.size": "Size",
  "unpack.group": "Group",
  "unpack.readonly": "read-only",
  "unpack.browse": "Browse the image",
  "unpack.up": "Up",
  "unpack.compressedBadge": "compressed",
  "unpack.readFile": "Read",
  "unpack.incompatible": "This file is not a partition container this tool can open.",
  "unpack.packageHint": "This is a package; extract from it with the other tool.",

  "inspect.hint": "Drop any image, package or partition here and the site will say what it is.",
  "inspect.container": "Container details",
  "inspect.digests": "Digests",
  "inspect.methods": "Patch methods",
  "inspect.browse": "Browse it in the unpack tool",
  "inspect.package": "This is a package",
  "inspect.readOnly": "This tool only looks: nothing is written and no image is produced.",

  "extract.browse": "Browse",
  "unpack.inside": "looking inside {entry} without extracting it",

  "logo.hint": "Drop a splash.img from an OPPO, Realme or OnePlus device here.",
  "logo.summary": "{frames} frames · header says {width}×{height} · image {size}",
  "logo.headerNote": "The header's size is not a bound on the frames; each frame keeps its own.",
  "logo.frames": "Frames",
  "logo.replaceHint": "Pick a frame and give it a new picture; every frame you leave alone keeps its exact bytes.",
  "logo.stored": "{width}×{height} · stored as {compressed} of {real}",
  "logo.replace": "Replace…",
  "logo.reset": "Reset",
  "logo.mode": "Adaptation",
  "logo.mode.direct": "As it is",
  "logo.mode.followOriginal": "Fit the frame (crop)",
  "logo.mode.autoAdapt": "Fit the frame (stretch)",
  "logo.mode.custom": "Custom size",
  "logo.width": "Width",
  "logo.height": "Height",
  "logo.uploadFailed": "This image could not be read in this browser.",
  "logo.pack": "Pack and download",
  "logo.packed": "Packed {count} replaced frame(s).",
  "logo.grows": "The result is {size} larger; a device partition is fixed, so it needs one at least that big.",
  "logo.same": "The result is the same size as the image you opened.",

  "logo.format.oppo-qualcomm": "OPPO / Realme / OnePlus splash (Qualcomm)",
  "logo.export": "Export all frames (.zip)",
  "logo.verified.identical": "Checked: the result is byte for byte the image you opened.",
  "logo.verified.framesIntact": "Checked: every frame you left alone kept its bytes.",
  "logo.verified.different": "Warning: the result differs from the image you opened.",

  "logo.batch": "Batch replace…",
  "logo.batchResult": "Applied {matched} of {count} pictures.",
  "logo.batchUnmatched": "No frame matches: {names}",

  "logo.format.mtk-logo": "MediaTek logo (Xiaomi and other MTK devices)",
  "logo.frameName": "Frame {index}",
  "logo.resolution": "Screen resolution",
  "logo.resolutionHint": "A MediaTek logo does not record the screen size, and the same block length fits several. Pick the one whose picture looks right; the candidates come from the size of the biggest block.",
  "logo.resolutionApply": "Use this size",

  "sparse.pack": "Pack as sparse image",
  "super.title": "Pack a super image",
  "super.hint": "Lay extracted partitions out as one super image, the way AOSP's lpmake does, so a flashing tool can take them.",
  "super.none": "Extract a partition first: any .img artifact in the workspace can go in.",
  "super.deviceSize": "Device size",
  "super.auto": "auto",
  "super.alignment": "Alignment",
  "super.alignment.mib": "1 MiB",
  "super.alignment.block": "block size",
  "super.pack": "Pack super image",

  "super.metadataOnly": "metadata only (super_empty)",
  "tool.animation.title": "Boot animation",
  "tool.animation.description": "Open a boot animation, play it, replace frames and pack it again.",
  "animation.hint": "Drop a bootanimation.zip here, or extract one from a system image and open it.",
  "animation.summary": "{parts} parts, {frames} frames",
  "animation.vendorDialect": "This desc.txt uses the offset line a vendor ships.",
  "animation.standardDialect": "This desc.txt uses the documented first line.",
  "animation.keepsOriginal": "Nothing is rewritten until you change it.",
  "animation.size": "Size",
  "animation.width": "Width",
  "animation.height": "Height",
  "animation.fps": "fps",
  "animation.play": "Play",
  "animation.pause": "Pause",
  "animation.parts": "Parts",
  "animation.partsHint": "How the system plays each part: how often, and how many frames to pause after it.",
  "animation.frames": "frames",
  "animation.count": "plays",
  "animation.pauseFrames": "pause",
  "animation.show": "Frames",
  "animation.framesHint": "Replace any frame; the rest keep their exact bytes.",
  "animation.replace": "Replace",
  "animation.previous": "Previous",
  "animation.next": "Next",
  "animation.replaced": "{count} replaced",
  "animation.pack": "Pack and download",
  "animation.packed": "Packed {count} replaced frame(s).",
  "animation.verified": "Checked: every frame you left alone kept its bytes.",
  "animation.differs": "Warning: the result differs from the archive you opened.",

  "animation.open": "Open in boot animation",
  "tool.diff.title": "Compare",
  "tool.diff.description": "Compare the image that is open with anything in the workspace, byte by byte.",
  "diff.hint": "Open the image you want to compare, then pick something in the workspace to compare it with.",
  "diff.noArtifacts": "The workspace is empty: extract or patch something first, then compare it with this image.",
  "diff.against": "Compare with",
  "diff.compare": "Compare",
  "diff.identical": "Identical: not one byte differs.",
  "diff.different": "{bytes} differ, in {ranges} range(s).",
  "diff.truncated": "the list is truncated; the byte count is exact",
  "diff.offset": "offset",
  "diff.length": "length",
  "diff.more": "and {count} more ranges",
  "diff.sections": "Where the difference is",
  "diff.sectionsHint": "The sections of a boot image, in file order, with what fell in each.",
  "diff.notBoot": "The open image is not a boot image, so there is nothing to name; the byte ranges above are the whole story.",

  "extract.selectAll": "Select all",
  "extract.selectNone": "Select none",
  "extract.extractSelected": "Extract {count} selected",
  "extract.selected": "{count} selected",

  "logo.setSize": "Set size",
  "task.extract": "Extracting",
  "task.sparse": "Packing sparse image",
  "task.super": "Building super image",
  "task.logo": "Reading frames",

  "shell.clearWorkspace": "Clear workspace",
  "shell.clearWorkspaceConfirm": "Forget everything this browser kept: the open file, the artifacts and anything saved for the next visit.",

  "dropzone.title": "Drop a boot image here",
  "dropzone.hint": "or click to browse files",
  "dropzone.busy": "Reading file",
  "dropzone.empty": "The selected file is empty.",
  "dropzone.tooLarge": "The file is larger than the supported limit.",
  "dropzone.formats.patch": "boot.img / init_boot.img / vendor_boot.img",
  "dropzone.formats.extract": "OTA .zip / payload.bin / vendor .img",
  "dropzone.formats.unpack": "super.img / sparse .img / erofs / ext4",
  "dropzone.formats.inspect": "boot / package / sparse / super / filesystem / logo",
  "dropzone.formats.logo": "splash.img / MTK logo.bin",
  "dropzone.formats.animation": "bootanimation.zip / rbootanimation.zip",
  "dropzone.formats.diff": "boot image / package / partition / any other file",

  "analyze.title": "Image Analysis",
  "analyze.newImage": "New image",
  "analyze.wasm": "WASM",
  "analyze.fallback": "TypeScript fallback",
  "analyze.parserNotes": "Parser notes",
  "analyze.technical": "Technical details",
  "analyze.technical.description":
    "Raw header fields, section offsets, per-section SHA-256 digests and parser warnings.",
  "analyze.methods": "Compatible Patch Methods",
  "analyze.methods.description": "Candidates are produced by the compatibility engine, not by the UI.",
  "analyze.method.notAvailable": "Not available",
  "analyze.method.compatible": "Compatible",
  "analyze.method.incompatible": "Incompatible",
  "analyze.method.select": "Select",
  "analyze.method.unavailable": "Unavailable",

  "patch.building": "Building the patch plan",
  "patch.plan": "Patch plan",
  "patch.plan.details": "Full plan record",
  "patch.plan.description":
    "Provider, release and artifact are pinned so the same plan can be reproduced later.",
  "patch.configuration": "Configuration",
  "patch.configuration.description": "Plan configuration passed to the provider.",
  "patch.pipeline": "Pipeline",
  "patch.pipeline.description": "The stages executed inside the Web Worker.",
  "patch.pipeline.complete": "Complete",
  "patch.notes": "Provider notes",
  "patch.planValue": "plan: {value}",
  "patch.field.provider": "Provider",
  "patch.field.release": "Release",
  "patch.field.artifact": "Artifact",
  "patch.field.artifactType": "Artifact type",
  "patch.field.artifactSha256": "Artifact SHA-256",
  "patch.field.architecture": "Architecture",
  "patch.field.target": "Target image",
  "patch.field.header": "Boot header",
  "patch.field.pageSize": "Page size",
  "patch.field.sourceSha256": "Source SHA-256",
  "patch.field.planId": "Plan id",
  "patch.field.reproducible": "Reproducible",
  "patch.value.notRecorded": "not recorded",
  "patch.value.yes": "yes",
  "patch.value.no": "no",
  "patch.value.none": "none",
  "patch.banner.upstream": "This runs the upstream {provider} implementation.",
  "patch.banner.mock.title": "This is the Mock Provider.",
  "patch.banner.mock.body":
    "It rewrites the kernel cmdline and writes a bootconfig manifest so the pipeline can be verified end to end. It does not root a device.",
  "patch.banner.apatch.body":
    "KernelPatch is injected into the kernel image inside boot.img by the upstream kptools build running in WebAssembly. The ramdisk is untouched, the original AVB signature is dropped, and flashing the result is your responsibility.",
  "patch.banner.kernelsu.body":
    "The ksuinit wrapper and the KernelSU module are written into the ramdisk by this browser build. The kernel is untouched, the original AVB signature is dropped, and flashing the result is your responsibility.",
  "patch.banner.magisk.body":
    "magiskinit and Magisk's payloads are written into the ramdisk by this browser build. The kernel is untouched, the original AVB signature is dropped, and flashing the result is your responsibility.",
  "patch.kpimg.title": "KernelPatch flavour",
  "patch.kpimg.description":
    "Which core image is injected. Each build only trusts its own manager app, so the manager below must be installed for the patch to be usable.",
  "patch.kpimg.custom": "Custom core image (attach your own)",
  "patch.kpimg.attach": "Attach a core image",
  "patch.kpimg.none": "no core image attached",
  "patch.kpimg.attached": "attached",
  "patch.kpimg.requiredManager": "required manager",
  "patch.kpimg.unknownManager": "unknown",
  "patch.kpimg.hint":
    "It has to be a KernelPatch core image (it starts with KP1158). The bytes travel with this run only, the plan records the file name, and the result reports the digest and the version kptools reads from it. Whatever manager it was built to trust is the one the device needs.",
  "patch.kpm.title": "KernelPatch modules (KPM)",
  "patch.kpm.description":
    "Optional. Each module is embedded into the patched kernel image. The bytes stay in your browser and are only handed to the patch worker for this run; the plan records the names.",
  "patch.kpm.attach": "Attach .kpm files",
  "patch.kpm.none": "plan: no modules",
  "patch.kpm.hint":
    "Modules are embedded exactly as provided. Whether a module loads at boot depends on the module and the kernel, and its licence is yours to check.",
  "patch.kernelsu.title": "Device KMI and module",
  "patch.kernelsu.description":
    "The KernelSU module has to match the kernel module interface of the device. It can only be read from an image that carries a kernel, so an init_boot image needs it selected here.",
  "patch.kernelsu.manager": "Manager",
  "patch.kernelsu.manager.description":
    "Which manager the patch is made for. Each one compiles its modules against its own signing certificate, so its module and the app that manages the result have to match.",
  "patch.kernelsu.selectKmi": "select the device KMI",
  "patch.kernelsu.override": "Override the bundled module",
  "patch.kernelsu.overrideWith": "Override with {name}",
  "patch.kernelsu.noModule": "plan: no module attached",
  "patch.kernelsu.hint.before":
    "This build already ships the module for the selected KMI and uses it by default; attaching one (named",
  "patch.kernelsu.hint.after": ") overrides it. Find the KMI with",
  "patch.kernelsu.hint.tail": "on the device: 6.6.118-android15-... means android15-6.6.",
  "patch.magisk.title": "Magisk options",
  "patch.magisk.description": "These are what Magisk reads at boot from .backup/.magisk.",
  "patch.magisk.flavor": "Manager",
  "patch.magisk.flavor.description":
    "Which manager's payloads are written. Magisk and WeaveMask patch the ramdisk the same way, but each app only manages the image made with its own payloads.",
  "patch.magisk.keepVerity": "Keep verity (KEEPVERITY)",
  "patch.magisk.keepVerity.body":
    "On keeps verity enabled and leaves fstab alone. Off removes magiskboot's verity flags from any fstab entry inside the ramdisk and drops verity_key.",
  "patch.magisk.keepForceEncrypt": "Keep forced encryption (KEEPFORCEENCRYPT)",
  "patch.magisk.keepForceEncrypt.body":
    "On keeps forced encryption and leaves fstab alone. Off removes the encryption flags magiskboot removes (forceencrypt, forcefdeorfbe, fileencryption).",
  "patch.magisk.preinit": "Pre-init storage (PREINITDEVICE)",
  "patch.magisk.preinit.placeholder": "auto (Magisk detects it)",
  "patch.magisk.preinit.hint.before": "Read it on the device with",
  "patch.magisk.preinit.hint.after":
    ", for example sda10. Leaving it empty lets Magisk detect it at boot.",
  "patch.superkey.title": "Root credentials",
  "patch.superkey.description":
    "Optional. Without a key the injected KernelPatch authenticates the manager by its signature. With one, the key is hashed into the kernel so an authorised client can use it and rotate it at runtime.",
  "patch.superkey.placeholder.set": "a superkey is set",
  "patch.superkey.placeholder.none": "no superkey (default)",
  "patch.superkey.hint":
    "The key is sent to the patch worker for this run only. Only its SHA-256 is written into the kernel; the plan, the metadata and the produced image never contain the key itself. Leave the field empty and apply to go back to the signature default.",
  "patch.output.title": "Output",
  "patch.output.description":
    "Device images are usually whole-partition dumps, so only the boot image itself is kept by default.",
  "patch.output.preserve": "Preserve the original image size",
  "patch.output.preserve.body":
    "Zero pads the output to {size} so tools that expect a partition sized image keep their file size. The AVB signature stays invalid either way.",
  "patch.output.keepSignature": "Keep the original AVB bytes",
  "patch.output.keepSignature.body":
    "The official patchers keep the signature area of the source image. Those bytes are stale after a patch either way, so verified boot fails with or without them; turn this off to leave the area empty instead.",
  "patch.back": "Back to analysis",
  "patch.start": "Start patch",
  "common.apply": "Apply",
  "common.clear": "Clear",

  "process.title": "Patching image",
  "process.subtitle": "{message} · all processing stays on this device.",
  "process.working": "Working",
  "process.aria.progress": "Patch progress",
  "process.cancel": "Cancel",
  "process.message.preparing": "Preparing",
  "process.message.complete": "Patch complete",
  "process.stage.analyze.label": "Analyze",
  "process.stage.analyze.description": "Reading the boot header",
  "process.stage.extract.label": "Extract",
  "process.stage.extract.description": "Splitting kernel and ramdisk",
  "process.stage.prepare.label": "Prepare",
  "process.stage.prepare.description": "Building the patch payload",
  "process.stage.patch.label": "Patch",
  "process.stage.patch.description": "Applying the provider pipeline",
  "process.stage.repack.label": "Repack",
  "process.stage.repack.description": "Rebuilding the boot image",
  "process.stage.verify.label": "Verify",
  "process.stage.verify.description": "Re-parsing and hashing the output",
  "process.stage.complete.label": "Complete",
  "process.stage.complete.description": "Output ready to download",
  "process.failed.title": "The patch did not finish",
  "process.failed.body":
    "Nothing is running now. The reason is below, and technical details are available.",
  "process.failed.back": "Back to the patch plan",
  "process.failed.restart": "Start over",
  "process.idle.title": "Nothing is being processed",
  "process.idle.body": "Start from an image, choose a patch method and press Start patch.",
  "process.idle.select": "Select an image",

  "result.title": "Patch complete",
  "result.subtitle": "The output image was repacked and re-verified in the browser.",
  "result.meta": "{size} · target {target} · provider {provider}",
  "result.verified": "Verified",
  "result.needsAttention": "Needs attention",
  "result.verification": "Verification",
  "result.compactNote":
    "Input {input} → output {output}. The input was a whole-partition image: partition padding and the AVB blob are not part of a boot image, so the output is the compact boot image that mkbootimg and Magisk also produce. The kernel itself is unchanged apart from the patch.",
  "result.paddedNote":
    "The output was zero padded to {size} so it keeps the original image size. The padding is not part of the boot image, and the AVB signature is still invalid.",
  "result.download": "Download image",
  "result.another": "Patch another image",
  "result.note.mock":
    "Produced by the Mock Provider. This output demonstrates the pipeline, not a root solution: it rewrites the kernel cmdline and the bootconfig manifest. Flashing it will not grant root.",
  "result.note.apatch":
    "Produced by {provider}{kernelPatch}. Only the kernel section was modified. The AVB signature was dropped, so verified boot will fail unless the image is re-signed or verification is disabled. ImageForge never flashes a device.",
  "result.note.withKernelPatch": " with KernelPatch {version}",
  "result.note.ramdisk":
    "Produced by {provider}. The ramdisk was modified and the kernel is untouched. The AVB signature was dropped, so verified boot will fail unless the image is re-signed or verification is disabled. ImageForge never flashes a device.",
  "result.warnings": "Warnings",
  "result.technical": "Technical details",
  "result.technical.description": "Patch metadata recorded by the engine and the provider.",
  "result.settingsLink": "Inspect the artifact registry and runtime settings",

  "settings.title": "Settings",
  "settings.subtitle": "Runtime, registry and appearance. Everything is stored locally.",
  "settings.version": "Version {version} · AGPL-3.0-or-later",
  "settings.appearance.title": "Appearance",
  "settings.appearance.description": "Design tokens switch between the light and dark themes.",
  "settings.language.title": "Language",
  "settings.language.description":
    "The interface language. Patch records, artifact digests and upstream names keep their own spelling.",
  "settings.runtime.title": "Runtime",
  "settings.runtime.description": "Heavy work executes off the main thread.",
  "settings.runtime.executor": "Executor",
  "settings.runtime.executor.worker": "Web Worker (Comlink RPC)",
  "settings.runtime.executor.inline": "Inline session (no Worker available)",
  "settings.runtime.wasm": "WASM module",
  "settings.runtime.wasm.loaded": "loaded {version}",
  "settings.runtime.wasm.fallback": "TypeScript fallback",
  "settings.runtime.wasm.checking": "checking",
  "settings.runtime.wasmPath": "WASM path",
  "settings.runtime.limit": "Image size limit",
  "settings.runtime.reason": "Reason: {reason}",
  "settings.artifacts.title": "Artifact registry",
  "settings.artifacts.description":
    "Versions, architectures and digests are declared here, never hardcoded in the UI.",
  "settings.artifacts.count": "{count} artifact(s)",
  "settings.artifacts.notRecorded": "not recorded",
  "settings.artifacts.source": "source {source}",
  "settings.artifacts.unknownSource": "unknown",
  "settings.artifacts.remote":
    "Remote artifact downloads are not part of this build. Upstream releases, their build systems and their licenses must be reviewed before a provider is implemented.",
  "settings.providers.title": "Patch providers",
  "settings.providers.description":
    "Providers only see the normalized image produced by the Image Engine.",
  "settings.providers.available": "Available",
  "settings.providers.planned": "Planned",
  "settings.providers.upstream": "upstream",
  "settings.license.title": "License",
  "settings.license.description": "ImageForge is AGPL-3.0-or-later.",
  "settings.license.body":
    "Third-party components keep their own licenses and are never re-licensed. See LICENSE, NOTICE and THIRD_PARTY_LICENSES/ in the repository root. No upstream root solution code is bundled in this build.",

  "diagnostics.export": "Export diagnostics",
  "readiness.title": "Before you write it to a device",
  "readiness.intro":
    "What this run recorded. ImageForge never writes to a device; these are the things to check on your side.",
  "readiness.target": "Target partition",
  "readiness.target.value": "The produced image is a {target} image and belongs in the {target} partition.",
  "readiness.manager": "Manager app",
  "readiness.manager.link": "Get {app} from its official release",
  "readiness.manager.value":
    "{manager} has to be installed on the device: it is the only manager this image trusts.",
  "readiness.manager.unknown":
    "A hand-supplied core image pairs with the manager it was built for; install that one.",
  "readiness.mock": "This output comes from the Mock Provider: it does not root a device.",
  "readiness.avb": "Verified boot",
  "readiness.avb.dropped":
    "The AVB signature was dropped, so verified boot fails unless you re-sign the image or disable verification.",
  "readiness.avb.kept":
    "The stale AVB bytes of the source image were kept; they are invalid after a patch either way, so verified boot fails unless you re-sign the image or disable verification.",
  "readiness.scope": "What changed",
  "readiness.scope.kernel":
    "Only the kernel section was patched; every other section, the ramdisk included, is byte for byte what you supplied.",
  "readiness.scope.ramdisk": "Only the ramdisk was patched; the kernel is byte for byte what you supplied.",
  "readiness.superkey": "Root credentials",
  "readiness.superkey.none":
    "No superkey is set: the injected KernelPatch authenticates the manager by its signature.",
  "readiness.superkey.custom":
    "A superkey is set and only its SHA-256 is in the kernel; keep the key, it is what authenticates an authorised client.",
  "readiness.modules": "Embedded modules",
  "readiness.modules.value":
    "{count} module(s) load together with the image; whether you may use them is your licence check.",
  "readiness.size": "Image size",
  "readiness.size.compact":
    "The output is the compact boot image ({size}); partition padding is not part of a boot image.",
  "readiness.size.padded": "The output was zero padded to {size} so it keeps the source partition size.",
  "readiness.verified": "Self check",
  "readiness.verified.pass": "All {count} checks passed. Plan id: {planId}.",
  "readiness.verified.fail": "{count} check(s) reported a problem; read them before using the image.",
  "readiness.restore": "Keeping a way back",
  "readiness.restore.kernel": "Keep a stock image of the same build: restoring means writing that image back.",
  "readiness.restore.ramdisk":
    "Keep a stock image of the same build. Magisk's own patcher can also restore itself from .backup/init.xz, but a stock image is the reliable way back.",

  "notfound.title": "This page does not exist",
  "notfound.back": "Back to the start",

  "error.showTechnical": "Show technical details",
  "error.hideTechnical": "Hide technical details",
  "error.technicalLabel": "technical detail",

  "code.value": "value",
  "code.copy": "Copy",
  "code.copied": "Copied",
  "code.aria.copy": "Copy value",

  "check.status.pass": "Passed",
  "check.status.warn": "Warning",
  "check.status.fail": "Failed",
  "check.size-limit": "Output size within limits",
  "check.structure": "Image structure valid",
  "check.format": "Image format matches the patch plan",
  "check.header-version": "Boot header version matches the patch plan",
  "check.ramdisk-payload": "Ramdisk payload readable",
  "check.ramdisk-hash": "Ramdisk SHA-256 matches the patch plan",
  "check.kernel-hash": "Kernel SHA-256 matches the patch plan",
  "check.cmdline": "Kernel cmdline carries the planned options",
  "check.bootconfig": "Bootconfig carries the patch manifest",

  "reason.not-implemented": "Not implemented in this build.",
  "reason.format": "Does not support {format} images.",
  "reason.header": "Boot header v{version} is outside the supported range.",
  "reason.architecture": "Architecture {architecture} is not supported.",
  "reason.no-kernel": "The image has no kernel section to patch.",
  "reason.no-ramdisk": "The image has no ramdisk section.",
  "reason.no-release": "No artifact release is registered for this provider.",

  "warning.unknown-architecture":
    "The kernel architecture could not be determined; architecture checks were skipped.",
  "warning.unsupported-kernel-compression":
    "The kernel payload is {compression}, which this build cannot expand; the kernel could not be patched safely.",
  "warning.unsupported-compression":
    "{compression} ramdisk payloads cannot be expanded in this build; the compressed payload would be copied unchanged.",
} as const;

export type MessageKey = keyof typeof en;

export type Messages = Record<MessageKey, string>;

export const MESSAGE_KEYS = Object.keys(en) as MessageKey[];
